import React, { memo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive, VStack, webOnlyStyles } from '@garnish/core';
import { useNavbarContainerHeight } from '@garnish/navigation';

import { useA11ySelector } from '@sg/a11y-helpers';

import { useIsLoggedIn } from '../../../context/AuthMachine';
import { useCustomer } from '../../../context/Customer';
import { useResetStacksPostLogout } from '../../../routing/hooks/useResetStacksPostLogout';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import { AccountCustomerName } from './components/AccountCustomerName';
import { AccountMenuFooter } from './components/AccountMenuFooter';
import { AccountMenuGiftCardHeader } from './components/AccountMenuGiftCardHeader';
import { AccountMenuSignOutItem } from './components/AccountMenuSignOutItem';
import { AccountPrimaryMenu } from './components/AccountPrimaryMenu';
import { AccountSecondaryMenu } from './components/AccountSecondaryMenu';

import type { AccountTabScreenId } from '../Account.types';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenu = memo((props: Props) => {
  const { screen: screenId } = props;

  const { currentBreakpoint, match, minWidth } = useResponsive();
  const { height: navbarHeight } = useNavbarContainerHeight();

  const a11ySelector = useA11ySelector('account.links');
  const resetStacksPostLogout = useResetStacksPostLogout();

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const { isSigningOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderGiftCardHeader = match([true, false]);
  const shouldRenderCustomerName = match([false, true]);
  const dividerColor = match([theme.colors.DARK_KALE, theme.colors.LIGHT]);

  const menuContainerGap = match([theme.spacing['6'], theme.spacing['10']]);
  const menuContainerWebStyle = webOnlyStyles({
    position: 'sticky',
    top: navbarHeight,
  } as unknown as Parameters<typeof webOnlyStyles>[0]);
  const menuContainerResponsiveStyle = match([
    styles.menuContainerXS,
    [styles.menuContainerSM, menuContainerWebStyle],
  ]);

  const primaryMenuGap = 0;
  const nameContainerStyle = match([styles.nameContainerXS, styles.nameContainerSM]);
  const secondaryMenuContainerStyles = [
    styles.secondaryMenuContainer,
    currentBreakpoint.isXS ? styles.secondaryMenuContainerBorderXS : undefined,
  ];
  const signOutItemContainerStyles = currentBreakpoint.isXS ? styles.signOutItemContainer : undefined; // prettier-ignore
  const signedOutFooterContainerStyle = currentBreakpoint.isXS ? styles.signedOutFooterContainer : undefined; // prettier-ignore

  // ─── Signed In Variant ───────────────────────────────────────────────

  if (isLoggedIn) {
    return (
      <VStack
        testID="account-menu"
        style={menuContainerResponsiveStyle}
        gap={menuContainerGap}
        hasDivider={minWidth.isSM}
        dividerColor={dividerColor}
        {...a11ySelector.props}
      >
        {shouldRenderCustomerName ? (
          <View style={nameContainerStyle}>
            <AccountCustomerName />
          </View>
        ) : null}

        {shouldRenderGiftCardHeader ? <AccountMenuGiftCardHeader /> : null}

        <AccountPrimaryMenu
          screenId={screenId}
          gap={primaryMenuGap}
          hasDivider={false}
          dividerColor={theme.colors.LIGHT}
        />

        {Platform.OS === 'web' ? null : (
          <View style={secondaryMenuContainerStyles}>
            <AccountSecondaryMenu />
          </View>
        )}

        <View style={signOutItemContainerStyles ? styles.signOutItemContainer : undefined}>
          <AccountMenuSignOutItem
            isSigningOut={isSigningOut}
            resetNavigation={resetStacksPostLogout}
          />
        </View>

        <AccountMenuFooter />
      </VStack>
    );
  }

  // ─── Signed Out Variant ──────────────────────────────────────────────

  return (
    <VStack
      testID="account-menu"
      style={menuContainerResponsiveStyle}
      gap={menuContainerGap}
      hasDivider={minWidth.isSM}
      dividerColor={dividerColor}
    >
      <AccountSignedOutMessage isEmbedded />

      {Platform.OS === 'web' ? null : (
        <View style={secondaryMenuContainerStyles}>
          <AccountSecondaryMenu />
        </View>
      )}

      <View style={signedOutFooterContainerStyle}>
        <AccountMenuFooter />
      </View>
    </VStack>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  menuContainerXS: {
    paddingVertical: theme.spacing['6'],
  },
  menuContainerSM: {
    paddingVertical: theme.spacing['12'],
    paddingHorizontal: theme.spacing['10'],
  },
  nameContainerXS: {
    paddingBottom: theme.spacing['5'],
  },
  nameContainerSM: {
    paddingBottom: theme.spacing['1'],
  },
  secondaryMenuContainer: {
    paddingTop: theme.spacing['4'],
  },
  secondaryMenuContainerBorderXS: {
    borderTopWidth: 1,
    borderColor: theme.colors.LIGHT,
  },
  signOutItemContainer: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.colors.LIGHT,
    paddingVertical: theme.spacing['4'],
  },
  signedOutFooterContainer: {
    borderTopWidth: 1,
    borderColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  screen?: AccountTabScreenId;
};
